import type {
  DeviceModel,
  DeviceModelPhysicalInput,
  DeviceModelPhysicalOutput,
  EquipmentType,
} from "@irriga-mais/common";
import type { DeviceModelId } from "@/api/model/device";

export const MODEL_LABELS: Record<DeviceModelId, string> = {
  LIC: "Controlador de Irrigação Localizado",
  "WPC-PL10": "Controlador de Bomba d'Água - PL10",
  "WPC-PL50": "Controlador de Bomba d'Água - PL50",
  VC: "Controlador de Válvula",
  RM: "Monitor de Reservatório",
} as const;

export const MODEL_ABBREVIATIONS: Record<DeviceModelId, string> = {
  LIC: "CI",
  "WPC-PL10": "CB-PL10",
  "WPC-PL50": "CB-PL50",
  VC: "CV",
  RM: "MN",
} as const;

export const MODEL_COMMERCIAL_NAMES: Record<DeviceModelId, string> = {
  LIC: "CODEK",
  "WPC-PL10": "PumpLink 10",
  "WPC-PL50": "PumpLink 50",
  VC: "ValveLink",
  RM: "LevelLink",
} as const;

export function resolveDeviceModelId(model: DeviceModelId | string | DeviceModel): DeviceModelId {
  if (typeof model === "object" && model !== null && "id" in model) {
    return model.id as DeviceModelId;
  }
  return model as DeviceModelId;
}

export function getDeviceModelLabel(model: DeviceModelId | string | DeviceModel): string {
  const modelId = resolveDeviceModelId(model);
  return MODEL_LABELS[modelId] || (typeof model === "object" && model.label) || "Modelo Desconhecido";
}
export function getDeviceModelAbbreviation(model: DeviceModelId | string | DeviceModel): string {
  const modelId = resolveDeviceModelId(model);
  return MODEL_ABBREVIATIONS[modelId] || "?";
}
export function getDeviceModelCommercialName(model: DeviceModelId | string | DeviceModel): string {
  const modelId = resolveDeviceModelId(model);
  return MODEL_COMMERCIAL_NAMES[modelId] || getDeviceModelLabel(model);
}
export function getDeviceModelIcon(model: DeviceModelId | string | DeviceModel): React.ReactNode {
  const modelId = resolveDeviceModelId(model);
  switch (modelId) {
    case "LIC":
      return <span className="text-green-600">📡</span>;
    case "WPC-PL10":
    case "WPC-PL50":
      return <span className="text-blue-600">💧</span>;
    case "VC":
      return <span className="text-yellow-600">🌱</span>;
    case "RM":
      return <span className="text-red-600">🏗️</span>;
    default:
      return <span className="text-gray-600">❓</span>;
  }
}

export function isLICDeviceModel(model: DeviceModelId | string | DeviceModel): boolean {
  return resolveDeviceModelId(model) === "LIC";
}

export function findDeviceModelsCompatibleOutputs(deviceModels: DeviceModel[], equipmentType: EquipmentType) {
  const result: Array<{
    model: DeviceModel;
    /**
     * The indexes of the outputs that are compatible with the equipment type.
     * it is one based, so the first output has index 1.
     */
    outputs: number[];
  }> = [];
  deviceModels.forEach((model) => {
    const compatibleOutputs: number[] = [];
    model.outputs?.forEach((output, index) => {
      const isCompatible = isOutputCompatibleWithEquipment(output, equipmentType);
      if (isCompatible) {
        compatibleOutputs.push(index + 1);
      }
    });
    if (compatibleOutputs.length > 0) {
      result.push({
        model,
        outputs: compatibleOutputs,
      });
    }
  });
  return result;
}

export function isOutputCompatibleWithEquipment(output: DeviceModelPhysicalOutput, equipmentType: EquipmentType) {
  return (
    !output.compatibleEquipmentTypes ||
    output.compatibleEquipmentTypes.length === 0 ||
    output.compatibleEquipmentTypes.includes(equipmentType)
  );
}

export function findDeviceModelsCompatibleInputs(deviceModels: DeviceModel[], equipmentType: EquipmentType) {
  const result: Array<{
    model: DeviceModel;
    /**
     * The indexes of the inputs that are compatible with the equipment type.
     * it is one based, so the first input has index 1.
     */
    inputs: number[];
  }> = [];
  deviceModels.forEach((model) => {
    const compatibleInputs: number[] = [];
    model.inputs?.forEach((input, index) => {
      const isCompatible = isInputCompatibleWithEquipment(input, equipmentType);
      if (isCompatible) {
        compatibleInputs.push(index + 1);
      }
    });
    if (compatibleInputs.length > 0) {
      result.push({
        model,
        inputs: compatibleInputs,
      });
    }
  });
  return result;
}

export function isInputCompatibleWithEquipment(input: DeviceModelPhysicalInput, equipmentType: EquipmentType) {
  return (
    !input.compatibleEquipmentTypes ||
    input.compatibleEquipmentTypes.length === 0 ||
    input.compatibleEquipmentTypes.includes(equipmentType)
  );
}
